
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  Clock, 
  Shield, 
  Smartphone,
  ArrowRight,
  Sparkles,
  UserPlus,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicNavbar } from '@/components/Layout/PublicNavbar';

const Home: React.FC = () => {
  const primaryColor = '#3d5a80';
  
  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Organiza eventos
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800 block">
                sin complicaciones
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              La plataforma todo-en-uno para planificar bodas, XV años y eventos especiales. 
              Gestiona invitados, proveedores y cronogramas desde un solo lugar.
            </p>
            <Link to="/signup">
              <Button size="lg" className="text-white px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:opacity-90" style={{ backgroundColor: primaryColor }}>
                Crea tu primer evento gratis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Como Funciona - Enhanced */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Cómo funciona
            </h2>
            <p className="text-xl text-gray-600">
              Tres pasos simples para organizar tu evento perfecto
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">

            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-white w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-shadow" style={{ backgroundColor: primaryColor }}>
                <UserPlus className="w-8 h-8" />
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg group-hover:shadow-xl transition-shadow border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Regístrate</h3>
                <p className="text-gray-600">
                  Crea tu cuenta gratuita en menos de 2 minutos y accede a todas las herramientas
                </p>
              </div>
            </div>

            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-white w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-shadow" style={{ backgroundColor: '#4a6fa5' }}>
                <Calendar className="w-8 h-8" />
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg group-hover:shadow-xl transition-shadow border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Crea tu evento</h3>
                <p className="text-gray-600">
                  Define los detalles de tu evento, presupuesto y comienza a planificar
                </p>
              </div>
            </div>

            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="text-white w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-shadow" style={{ backgroundColor: '#5a7fc7' }}>
                <Send className="w-8 h-8" />
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg group-hover:shadow-xl transition-shadow border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Envía invitaciones</h3>
                <p className="text-gray-600">
                  Gestiona tu lista de invitados y envía invitaciones personalizadas vía WhatsApp
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Características */}
      <section id="caracteristicas" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Todo lo que necesitas para tu evento
            </h2>
            <p className="text-xl text-gray-600">
              Herramientas profesionales al alcance de todos
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: primaryColor }}>
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Gestión de Invitados
              </h3>
              <p className="text-gray-600">
                Organiza tu lista de invitados, controla confirmaciones y envía recordatorios automáticos
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: '#4a6fa5' }}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Catálogo de Proveedores
              </h3>
              <p className="text-gray-600">
                Encuentra y gestiona todos tus proveedores desde fotógrafos hasta catering
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                WhatsApp Integrado
              </h3>
              <p className="text-gray-600">
                Envía invitaciones y recordatorios directamente por WhatsApp con un solo clic
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Cronograma Detallado
              </h3>
              <p className="text-gray-600">
                Planifica cada detalle de tu evento con cronogramas precisos y recordatorios
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
              <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Control de Presupuesto
              </h3>
              <p className="text-gray-600">
                Mantén tu presupuesto bajo control con seguimiento en tiempo real de gastos
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
              <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Acceso Móvil
              </h3>
              <p className="text-gray-600">
                Gestiona tu evento desde cualquier lugar con nuestra plataforma responsive
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Secondary - Enhanced with gradient */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(90deg, rgba(61, 90, 128, 1) 0%, rgba(61, 110, 128, 1) 50%, rgba(89, 61, 128, 1) 100%)'
        }}
      >
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full bg-repeat" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <Sparkles className="w-16 h-16 text-white mx-auto mb-6 animate-pulse" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              ¿Listo para planificar tu evento perfecto?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Únete a miles de personas que ya confían en CoPlanner para sus eventos especiales
            </p>
            <Link to="/signup">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105" style={{ color: primaryColor }}>
                Crea tu primer evento gratis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-8 h-8" style={{ color: primaryColor }} />
                <h3 className="text-2xl font-bold" style={{ color: primaryColor }}>CoPlanner</h3>
              </div>
              <p className="text-gray-400">
                La plataforma más completa para organizar eventos inolvidables
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Enlaces rápidos</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#caracteristicas" className="hover:text-white transition-colors">Características</a></li>
                <li><Link to="/precios" className="hover:text-white transition-colors">Precios</Link></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Iniciar sesión</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/terminos" className="hover:text-white transition-colors">Términos de uso</Link></li>
                <li><Link to="/aviso-de-privacidad" className="hover:text-white transition-colors">Política de privacidad</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Contacto</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Síguenos</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Facebook</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Instagram</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Twitter</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 CoPlanner. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
